import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Eye, EyeOff, Lock, Mail, Moon, Sun, Shield, ArrowRight } from 'lucide-react';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { isDark, toggleTheme, colors } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex transition-colors duration-300"
      style={{ backgroundColor: colors.background }}
    >
      {/* Left Panel – Branding */}
      <div
        className="hidden lg:flex lg:w-[45%] xl:w-[40%] flex-col justify-between p-10 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 50%, #8B5CF6 100%)',
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }}
          />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div>
              <h2 className="text-white font-bold text-xl tracking-tight">Manam</h2>
              <p className="text-white/60 text-xs font-medium">Admin Dashboard</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-6">
          <div>
            <h1 className="text-white text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
              Manage your
              <br />
              properties with
              <br />
              <span className="text-white/80">confidence.</span>
            </h1>
            <p className="mt-4 text-white/60 text-base leading-relaxed max-w-md">
              A powerful admin panel to monitor bookings, verify hosts,
              manage users, and oversee the entire property ecosystem.
            </p>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {['Real-time Analytics', 'User Management', 'Payout Tracking', 'Host Verification'].map((feature) => (
              <div
                key={feature}
                className="px-3.5 py-1.5 rounded-full text-xs font-medium text-white/80 bg-white/10 border border-white/10 backdrop-blur-sm"
              >
                {feature}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} Manam. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel – Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 relative">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="absolute top-6 right-6 p-3 rounded-xl transition-all"
          style={{
            backgroundColor: colors.surfaceHover,
            border: `1px solid ${colors.border}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.borderHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.border;
          }}
        >
          {isDark ? (
            <Sun className="w-4 h-4" style={{ color: '#FBBF24' }} />
          ) : (
            <Moon className="w-4 h-4" style={{ color: '#64748B' }} />
          )}
        </button>

        <div className="max-w-[380px] w-full space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center">
            <div
              className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              }}
            >
              <span className="text-xl font-bold text-white">M</span>
            </div>
          </div>

          {/* Title */}
          <div>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight"
              style={{ color: colors.text }}
            >
              Welcome back
            </h2>
            <p
              className="mt-2 text-sm"
              style={{ color: colors.textSecondary }}
            >
              Sign in to your admin account to continue
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-[13px] font-semibold mb-2"
                style={{ color: colors.text }}
              >
                Email address
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]"
                  style={{ color: colors.textMuted }}
                />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full pl-11 pr-4 py-3 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
                  style={{
                    backgroundColor: colors.backgroundSecondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.borderActive;
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="admin@manam.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-[13px] font-semibold mb-2"
                style={{ color: colors.text }}
              >
                Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]"
                  style={{ color: colors.textMuted }}
                />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="w-full pl-11 pr-12 py-3 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
                  style={{
                    backgroundColor: colors.backgroundSecondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.borderActive;
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 p-1 rounded-lg transition-colors"
                  style={{ color: colors.textMuted }}
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.surfaceHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-white text-[14px] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div
            className="flex items-start gap-3 p-3.5 rounded-xl"
            style={{
              backgroundColor: colors.primaryGhost,
              border: `1px solid ${colors.primaryBorder}`,
            }}
          >
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.primary }} />
            <p className="text-[12px] leading-relaxed" style={{ color: colors.textSecondary }}>
              This dashboard is restricted to authorized administrators only.
              All access is monitored and logged.
            </p>
          </div>

          {/* Footer (mobile) */}
          <p
            className="lg:hidden text-center text-xs"
            style={{ color: colors.textMuted }}
          >
            © {new Date().getFullYear()} Manam. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;